import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Volleyball, Sneaker, Barbell, HandFist, Users } from "@phosphor-icons/react";
import axios from "axios";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { GridSkeleton } from "@/components/Skeleton";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const sportsData = [
  { id: "futsal", title: "Futsal", price: 0, category: "Esporte", icon: Volleyball, color: "from-green-500 to-emerald-500" },
  { id: "capoeira", title: "Capoeira", price: 0, category: "Esporte", icon: Sneaker, color: "from-yellow-500 to-amber-500" },
  { id: "funcional", title: "Funcional", price: 0, category: "Esporte", icon: Barbell, color: "from-blue-500 to-indigo-500" },
  { id: "jiujitsu", title: "Jiu-Jitsu", price: 0, category: "Luta", icon: HandFist, color: "from-red-500 to-orange-500" },
  { id: "box", title: "Box", price: 0, category: "Luta", icon: HandFist, color: "from-slate-600 to-slate-800" },
  { id: "muaythai", title: "Muay Thai", price: 0, category: "Luta", icon: HandFist, color: "from-orange-600 to-red-600" }
];

const Sports = () => {
  const navigate = useNavigate();
  const { getAuthHeaders } = useAuth();
  const [capacity, setCapacity] = useState({});
  const [loading, setLoading] = useState(true);
  const today = new Date().toISOString().split("T")[0];

  useEffect(() => {
    fetchCapacity();
  }, []);

  const fetchCapacity = async () => {
    try {
      const results = await Promise.all(
        sportsData.map((s) =>
          axios.get(`${BACKEND_URL}/api/sports/capacity/${s.id}?date=${today}`, {
            headers: getAuthHeaders(), withCredentials: true,
          }).then((r) => [s.id, r.data]).catch(() => [s.id, { booked: 0, max_capacity: 20, available: 20 }])
        )
      );
      setCapacity(Object.fromEntries(results));
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const handleBuy = (item) => {
    navigate("/pix-payment", {
      state: { itemId: item.id, itemType: "product", itemInfo: { title: item.title, description: item.category, price: item.price } }
    });
  };

  const categories = [...new Set(sportsData.map(s => s.category))];

  if (loading) return <div><h2 className="text-2xl sm:text-3xl font-bold text-[#042F2E] mb-6">Esporte & Luta</h2><GridSkeleton count={6} /></div>;

  return (
    <div>
      <h2 className="text-2xl sm:text-3xl font-bold text-[#042F2E] mb-2">Esporte & Luta</h2>
      <p className="text-[#115E59] mb-8">Todas as atividades esportivas são <strong className="text-[#10B981]">GRATUITAS</strong> para membros!</p>

      {categories.map(cat => (
        <div key={cat} className="mb-10">
          <h3 className="text-lg font-bold text-[#115E59] uppercase tracking-wider mb-4">{cat}</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {sportsData.filter(s => s.category === cat).map((item, index) => {
              const IconComp = item.icon;
              const cap = capacity[item.id] || { booked: 0, max_capacity: 20, available: 20 };
              const full = cap.available <= 0;
              return (
                <motion.div
                  key={item.id}
                  initial={{ opacity: 0, y: 15 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                  className="ticket-card bg-white border border-[#E4DFD3] rounded-2xl p-6 shadow-sm"
                  data-testid={`sport-card-${item.id}`}
                >
                  <div className={`bg-gradient-to-br ${item.color} w-14 h-14 rounded-xl flex items-center justify-center mb-4 shadow-md`}>
                    <IconComp size={28} weight="duotone" className="text-white" />
                  </div>
                  <h4 className="text-xl font-bold text-[#042F2E] mb-1">{item.title}</h4>
                  <p className="text-xs text-[#115E59] mb-2 uppercase tracking-wider font-bold">{item.category}</p>
                  <div className="flex items-center gap-2 mb-3">
                    <Users size={16} weight="duotone" className="text-[#115E59]" />
                    <span className={`text-sm font-bold ${full ? "text-red-500" : "text-[#115E59]"}`}>
                      {cap.booked}/{cap.max_capacity} vagas
                    </span>
                    {!full && (
                      <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full font-bold">
                        {cap.available} disponíveis
                      </span>
                    )}
                    {full && (
                      <span className="text-xs bg-red-100 text-red-600 px-2 py-0.5 rounded-full font-bold">Lotado</span>
                    )}
                  </div>
                  <div className="text-2xl font-black text-[#10B981] mb-4">GRÁTIS</div>
                  <Button
                    onClick={() => handleBuy(item)}
                    disabled={full}
                    className={`w-full font-bold rounded-full py-4 text-sm ${full ? "bg-gray-300 text-gray-500 cursor-not-allowed" : "bg-[#10B981] hover:bg-[#059669] text-white"}`}
                    data-testid={`book-sport-${item.id}`}
                  >
                    {full ? "Sem Vagas" : "Agendar Grátis"}
                  </Button>
                </motion.div>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
};

export default Sports;
